import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { getForcedTheme, isValidTheme, getRouteTheme } from '../../utils/swiss-theme-utils';

interface RouteThemeWrapperProps {
  children: React.ReactNode;
}

/**
 * RouteThemeWrapper - Lightweight theme management using next-themes best practices
 *
 * Key principle: Set theme ONCE on mount, then let user control it
 * Follows expert guidance to avoid fighting with next-themes
 *
 * This component:
 * 1. Sets theme ONCE on mount based on route and URL
 * 2. Lets next-themes handle all DOM manipulation 
 * 3. Avoids infinite loops from multiple useEffect hooks
 *
 * Must be rendered inside <Router> as it uses useLocation()
 */
const RouteThemeWrapper: React.FC<RouteThemeWrapperProps> = ({ children }) => {
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch with next-themes
  useEffect(() => {
    setMounted(true);
  }, []);

  // SINGLE effect that runs ONLY on mount - prevents infinite loops
  useEffect(() => {
    if (!mounted) return;

    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const urlTheme = searchParams.get('theme');

    console.log('🎨 Theme effect:', {
      mounted,
      pathname,
      currentTheme: theme,
      urlTheme,
      forcedTheme: getForcedTheme(pathname)
    });

    // Priority 1: Is this a forced-theme route?
    const forcedTheme = getForcedTheme(pathname);
    if (forcedTheme) {
      if (theme !== forcedTheme) {
        setTheme(forcedTheme);
      }
      return; // Stop - don't check URL
    }

    // Priority 2: Does URL have a valid theme parameter?
    if (urlTheme && isValidTheme(urlTheme)) {
      if (theme !== urlTheme) {
        setTheme(urlTheme);
      }
      return; // Stop - URL wins
    }

    // Priority 3: Use stored/default theme (next-themes handles this)
    // Do nothing - let next-themes load from localStorage

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]); // CRITICAL: Only [mounted] in deps - prevents re-running on theme/pathname changes

  // Don't render until mounted (prevents hydration issues)
  if (!mounted) {
    return null;
  }

  // Let next-themes handle all class application - remove custom swiss class wrapper
  return <>{children}</>;
};

export default RouteThemeWrapper;